import { useSiteDetection, useToggle } from 'shared/hooks';
import { Header, LwButton, Table, Toolbox, useOnScrollDown } from 'redesign';
import { FilterButton } from 'ogp/components/views/administration/shared';
import { RenderUnsettledUI } from 'shared/components';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  styled,
  Typography,
} from '@mui/material';
import { useLevelPagination } from 'ogp/hooks/queryHooks/misc/use-level-pagination';
import { PaginationIndicator } from 'ogp/components/common/pagination-indicator';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAlert } from 'shared/components/alerts';
import { useQueryClient } from 'react-query';
import { QUERY_KEYS } from 'api/query-keys';
import { partnerPlannerService } from 'partner/shared/services';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { IconCaretRight } from 'assets/img';
import { useTranslation } from 'react-i18next';
import { useGetShiftPlanningShifts } from './hooks/use-get-shift-planning-shifts';
import {
  INITIAL_PAGE,
  PartnerShiftPlanningColumnDefinition,
  PartnerShiftPlanningTableOptions,
} from './shift-planning-table.types';
import {
  PreferredWorkersColumnDefinition,
  PreferredWorkersTableOptions,
} from './preferred-workers-table.types';
import {
  PreferredWorkerProposalAction,
  useActionOnPreferredWorkerProposal,
} from './hooks/use-action-on-preferred-worker-proposal';
import { getShiftPlanningColumnDefinition } from './utils/get-shift-planning-column-definition';
import { getPreferredWorkersColumnDefinition } from './utils/get-preferred-workers-column-definition';
import { useShiftPlanningPage } from './hooks/use-shift-planning-page';
import { getPartnerPlanningShiftsTableData } from './utils/get-partner-shift-planning-table-data';
import { getPreferredWorkersTableData } from './utils/get-preferred-workers-table-data';
import { useGetPreferredWorkerProposals } from './hooks/use-get-preferred-worker-proposals';
import { useDynamicTableHeight } from './hooks/use-dynamic-table-height';
import { ShiftPlanningFiltersDataProvider } from '../filters/shift-planning-filters-data-provider';

const PARTNER_PLANNING_PAGE_SIZE = 5;

type ExpandedPageSections = {
  preferredWorkers: boolean;
  assignedShifts: boolean;
};

export const ShiftPlanningTable = ({
  openShiftDetail,
}: {
  openShiftDetail: (shiftId: string) => void;
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const preferredWorkerAccordionRef = useRef<HTMLDivElement>(null);

  const { height, paddingBottom } = useDynamicTableHeight(headerRef, preferredWorkerAccordionRef);

  const { site } = useSiteDetection();

  const { filters: appliedFilters, setFilters, selectedFilterCount } = useShiftPlanningPage();

  const { allowPreferredWorkers } = useFeatureFlagContext();
  const { alertSuccess, alertError } = useAlert();

  const { t } = useTranslation('partner-screen-specific');

  const filters = {
    fromDate: appliedFilters.fromDate ?? undefined,
    company: appliedFilters.company ?? undefined,
    branchOfficeIds: appliedFilters.branchOffices ?? undefined,
    departmentIds: appliedFilters.departments ?? undefined,
    jobType: appliedFilters.jobType ?? undefined,
    filledStatus: appliedFilters.filledStatus ?? undefined,
    workerId: appliedFilters.worker ?? undefined,
    isFreelance: appliedFilters.isFreelance ?? undefined,
    isTempWork: appliedFilters.isTempWork ?? undefined,
    pageParam: INITIAL_PAGE,
    pageSizeParam: PARTNER_PLANNING_PAGE_SIZE,
  };

  const shiftsQuery = useGetShiftPlanningShifts(filters);
  const pagination = useLevelPagination(0, 5);
  const preferredWorkersQuery = useGetPreferredWorkerProposals(
    site,
    { ...filters, page: pagination.page, pageSize: pagination.size },
    allowPreferredWorkers
  );
  const mutation = useActionOnPreferredWorkerProposal();

  const queryClient = useQueryClient();

  const { data: shifts } = shiftsQuery;
  const { data: preferredWorkers } = preferredWorkersQuery;

  const flatShifts = useMemo(() => shifts?.pages?.flatMap((page) => page.items) ?? [], [shifts]);
  const flatWorkers = useMemo(() => preferredWorkers?.items ?? [], [preferredWorkers]);

  const shiftsTable = useMemo(() => getPartnerPlanningShiftsTableData(flatShifts), [flatShifts]);
  const shiftColumns = useMemo(() => getShiftPlanningColumnDefinition(t), []);

  const preferredWorkersTableOptions: PreferredWorkersTableOptions = useMemo(
    () => ({
      onAcceptWorker: (proposalId: string) => {
        mutation.mutate(
          { proposalId, action: PreferredWorkerProposalAction.ACCEPT },
          {
            onSuccess: () => {
              alertSuccess(`Werker op shift geplaatst`);
              queryClient.invalidateQueries(QUERY_KEYS.shifts());
            },

            onError: (e) => {
              alertError(e);
            },
          }
        );
      },
      onRejectWorker: (proposalId: string) => {
        mutation.mutate(
          { proposalId, action: PreferredWorkerProposalAction.REJECT },
          {
            onSuccess: () => {
              alertSuccess(`Werker niet op shift geplaatst`);
              queryClient.invalidateQueries(QUERY_KEYS.shifts());
            },

            onError: (e) => {
              alertError(e);
            },
          }
        );
      },
    }),
    [alertSuccess]
  );

  const workersTable = useMemo(
    () => getPreferredWorkersTableData(flatWorkers, preferredWorkersTableOptions),
    [flatWorkers, preferredWorkersTableOptions]
  );
  const workersColumns = useMemo(() => getPreferredWorkersColumnDefinition(t), []);
  const workersRowsCount = useMemo(
    () => preferredWorkersQuery.data?.totalRows ?? 0,
    [preferredWorkersQuery.data]
  );

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: shiftsQuery,
    flatData: flatShifts,
  });

  const { on: filtersOpen, setOn, setOff } = useToggle();
  const [expandedSections, setExpandedSections] = useState<ExpandedPageSections>({
    preferredWorkers: true,
    assignedShifts: true,
  });

  const exportShifts = useCallback(async () => {
    try {
      await partnerPlannerService.createPlanningExport({
        fromDate: appliedFilters.fromDate,
        companyId: appliedFilters.company,
        branchOffices: appliedFilters.branchOffices,
        departments: appliedFilters.departments,
        jobTypeIds: appliedFilters.jobType,
        workerId: appliedFilters.worker,
        isFreelance: appliedFilters.isFreelance,
        isTempWork: appliedFilters.isTempWork,
      });
      alertSuccess(
        'Export succesvol aangevraagd. Binnen enkele minuten kunt u een email verwachten.'
      );
    } catch (error) {
      alertError(error);
    }
  }, [alertError, alertSuccess]);

  if (shiftsQuery.status !== 'success') {
    return <RenderUnsettledUI data={shiftsQuery} />;
  }
  if (allowPreferredWorkers && preferredWorkersQuery.status !== 'success') {
    return <RenderUnsettledUI data={preferredWorkersQuery} />;
  }
  return (
    <>
      <Box ref={headerRef}>
        <Header titleText="Shift Planning" />
        <Toolbox underline={false}>
          <Toolbox.PushRight>
            <LwButton color="secondary" onClick={exportShifts}>
              Export
            </LwButton>
            <FilterButton onClick={setOn} nrOfActiveFilters={selectedFilterCount} />
          </Toolbox.PushRight>
        </Toolbox>
      </Box>
      <PlanningContent>
        {allowPreferredWorkers ? (
          <StyledAccordion
            ref={preferredWorkerAccordionRef}
            expanded={expandedSections.preferredWorkers}
            onChange={() =>
              setExpandedSections((prev) => ({ ...prev, preferredWorkers: !prev.preferredWorkers }))
            }
          >
            <StyledAccordionSummary
              expandIcon={
                <SectionStateIcon isCollapsed={!expandedSections.preferredWorkers}>
                  <IconCaretRight width={16} height={16} />
                </SectionStateIcon>
              }
            >
              <Typography variant="h3">Voorkeursflexwerkers</Typography>
            </StyledAccordionSummary>
            <PreferredWorkerAccordionDetails>
              <Box height={'80%'}>
                <Table<PreferredWorkersColumnDefinition, PreferredWorkersTableOptions>
                  stickyHeader={true}
                  columns={workersColumns}
                  data={workersTable}
                  tableStyle={{ tableLayout: 'auto' }}
                  data-testid="preferred-workers-virtual-table"
                  onAcceptWorker={preferredWorkersTableOptions.onAcceptWorker}
                  onRejectWorker={preferredWorkersTableOptions.onRejectWorker}
                />
              </Box>
              <Box height={'20%'}>
                <PaginationIndicator
                  pageCount={workersRowsCount}
                  pagination={pagination}
                  customRowsPerPageOptions={[5]}
                />
              </Box>
            </PreferredWorkerAccordionDetails>
          </StyledAccordion>
        ) : null}

        <StyledShiftAccordion
          expanded={expandedSections.assignedShifts}
          onChange={() =>
            setExpandedSections((prev) => ({ ...prev, assignedShifts: !prev.assignedShifts }))
          }
        >
          <StyledAccordionSummary
            expandIcon={
              <SectionStateIcon isCollapsed={!expandedSections.assignedShifts}>
                <IconCaretRight width={16} height={16} />
              </SectionStateIcon>
            }
          >
            <Typography variant="h3">Shifts</Typography>
          </StyledAccordionSummary>
          <StyledTableDetails>
            <Box height={height} maxHeight={height} pb={paddingBottom}>
              <Table<PartnerShiftPlanningColumnDefinition, PartnerShiftPlanningTableOptions>
                stickyHeader={true}
                columns={shiftColumns}
                data={shiftsTable}
                tableStyle={{ tableLayout: 'fixed' }}
                onScroll={fetchIfBottomScrolled}
                data-testid="shift-planning-virtual-table-2"
                openShiftDetail={openShiftDetail}
              />
            </Box>
          </StyledTableDetails>
        </StyledShiftAccordion>
      </PlanningContent>
      {filtersOpen ? (
        <ShiftPlanningFiltersDataProvider
          appliedFilters={appliedFilters}
          handleFiltersChange={setFilters}
          onClose={setOff}
        />
      ) : null}
    </>
  );
};

const PlanningContent = styled(Box)(() => ({
  padding: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
}));

const StyledAccordion = styled(Accordion)<{ expanded: boolean }>(() => ({
  justifyContent: 'flex-start',
  boxShadow: 'none',
  '& .MuiAccordionSummary-expandIconWrapper': {},
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordion-root:before': {
    backgroundColor: 'white',
  },
}));

const StyledShiftAccordion = styled(Accordion)<{ expanded: boolean }>(({ expanded }) => ({
  flex: expanded ? 0 : 1,
  justifyContent: 'flex-start',
  boxShadow: 'none',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordion-root:before': {
    backgroundColor: 'white',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  flexDirection: 'row-reverse',
  gap: theme.spacing(2),
}));

const SectionStateIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: theme.spacing(6),
  height: theme.spacing(6),
  backgroundColor: isCollapsed ? theme.palette.lwPrimaryAccentLight[10] : 'white',
  borderRadius: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const PreferredWorkerAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
  overflow: 'scroll',
  maxHeight: '55vh',
  minHeight: '3vh',
}));

const StyledTableDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
}));
